import { canShareFortuneImage, imageFileDataUrl, saveFortuneImage, sharePreparedFortuneImage, type FortuneSaveMode } from '../../fortune-save';
import type { FortuneSavePolicy } from '../../fortune-save-policy';
import { escapeHtml } from '../format';
import { NAME_IMAGE_WARNING } from './copy';
import { downloadFile } from './dom';
import type { UiEnvironment } from './environment';
import { bindInAppNotice, inAppNoticeMarkup } from './inapp-notice';
import type { Toast } from './toast';

/**
 * Image saving for the report (spec §6.3). Reuses the fortune save policy
 * (native share on iOS, preview in in-app browsers, download elsewhere) with
 * our own preview dialog, because the fortune dialog's copy is fortune-only.
 *
 * Unlike the fortune tool this never uploads the PNG to the image server for
 * in-app browsers: the image carries people's names, and results stay on the
 * device. Those browsers get a long-press preview plus "open in a browser".
 */

export interface SaveImageContext {
  readonly environment: UiEnvironment;
  readonly toast: Toast;
}

let openDialog: HTMLDialogElement | null = null;

export function showReportImagePreview(
  file: File,
  policy: FortuneSavePolicy,
  shareFailed: boolean,
  context: SaveImageContext,
  returnFocusTo?: HTMLElement,
): void {
  openDialog?.close();
  const dialog = document.createElement('dialog');
  openDialog = dialog;
  dialog.className = 'krc-save-dialog';
  dialog.setAttribute('aria-labelledby', 'krc-save-title');
  dialog.setAttribute('aria-describedby', 'krc-save-warning');
  const canShare = canShareFortuneImage(file);
  const inApp = policy.download === 'http';
  const description = policy.os === 'ios'
    ? (canShare ? '‘사진 앱에 저장’을 누른 뒤 공유 메뉴에서 ‘이미지 저장’을 골라 주세요.' : '아래 이미지를 길게 눌러 사진에 저장해 주세요.')
    : inApp
      ? '이 앱의 브라우저에서는 다운로드가 막힐 수 있어요. 아래 이미지를 길게 눌러 저장해 보세요.'
      : '‘이미지 다운로드’를 눌러 성적표를 저장해 주세요.';
  dialog.innerHTML = `
    <div class="krc-save-heading">
      <h2 id="krc-save-title">성적표 이미지 저장</h2>
      <button class="krc-save-close" type="button" aria-label="이미지 저장 닫기">×</button>
    </div>
    <p class="krc-save-warning" id="krc-save-warning">${escapeHtml(NAME_IMAGE_WARNING)}</p>
    <p class="krc-save-description">${escapeHtml(description)}</p>
    <div class="krc-save-buttons">
      ${canShare ? `<button class="button button-primary" type="button" data-save-share>${policy.os === 'ios' ? '사진 앱에 저장' : '공유 메뉴 열기'}</button>` : ''}
      <a class="button ${canShare && policy.os === 'ios' ? 'button-soft' : 'button-primary'}" data-save-download download="${escapeHtml(file.name)}" hidden>이미지 다운로드</a>
    </div>
    <p class="krc-save-status" role="status" aria-live="polite"></p>
    <img class="krc-save-image" alt="저장할 카톡 성적표 이미지 미리보기" width="1080" height="1350" hidden />
    ${inApp ? inAppNoticeMarkup(context.environment, 'save') : ''}
    <p class="krc-save-help">${policy.os === 'ios' ? '‘이미지 다운로드’는 파일 앱에 저장될 수 있어요.' : '받은 이미지는 갤러리나 내 파일의 다운로드 폴더에서 찾을 수 있어요.'}</p>`;

  const status = dialog.querySelector<HTMLElement>('.krc-save-status')!;
  const image = dialog.querySelector<HTMLImageElement>('.krc-save-image')!;
  const download = dialog.querySelector<HTMLAnchorElement>('[data-save-download]')!;
  const share = dialog.querySelector<HTMLButtonElement>('[data-save-share]');
  let objectUrl: string | null = null;
  let closed = false;
  const showStatus = (message: string) => {
    if (!closed) status.textContent = message;
  };

  share?.addEventListener('click', async () => {
    share.disabled = true;
    const mode = await sharePreparedFortuneImage(file);
    share.disabled = false;
    if (mode === 'unavailable') showStatus('공유 메뉴를 열지 못했어요. 아래 이미지를 길게 눌러 저장해 주세요.');
    else if (mode === 'shared') showStatus('공유 메뉴를 닫았어요. 저장한 위치를 확인해 주세요.');
    else showStatus('');
  });
  download.addEventListener('click', () => showStatus('다운로드를 요청했어요. 저장 위치를 확인해 주세요.'));
  dialog.querySelector('.krc-save-close')!.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    closed = true;
    if (openDialog === dialog) openDialog = null;
    dialog.remove();
    document.documentElement.classList.remove('krc-dialog-open');
    if (objectUrl) {
      const url = objectUrl;
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    if (returnFocusTo?.isConnected) returnFocusTo.focus({ preventScroll: true });
  }, { once: true });

  bindInAppNotice(dialog, context.environment, context.toast);
  document.body.append(dialog);
  try {
    dialog.showModal();
  } catch {
    // Very old browsers without <dialog>: fall back to a plain download.
    closed = true;
    openDialog = null;
    dialog.remove();
    downloadFile(file);
    return;
  }
  document.documentElement.classList.add('krc-dialog-open');
  dialog.querySelector<HTMLButtonElement>('.krc-save-close')!.focus();

  if (policy.download === 'blob-url') {
    objectUrl = URL.createObjectURL(file);
    image.src = objectUrl;
    image.hidden = false;
    download.href = objectUrl;
    download.hidden = false;
  } else {
    void imageFileDataUrl(file).then((dataUrl) => {
      if (closed) return;
      image.src = dataUrl;
      image.hidden = false;
      download.href = dataUrl;
      download.hidden = false;
    }).catch(() => showStatus('미리보기를 열지 못했어요. 창을 닫고 다시 시도해 주세요.'));
  }
  if (shareFailed) showStatus('공유 메뉴를 열지 못했어요. 아래에서 다른 방법을 골라 주세요.');
}

/** Call straight from the save tap with an already rendered File. */
export function saveReportImage(file: File, button: HTMLElement, context: SaveImageContext): Promise<FortuneSaveMode> {
  return saveFortuneImage(file, button, {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    canShare: canShareFortuneImage,
    share: sharePreparedFortuneImage,
    preview: (image, policy, shareFailed) => showReportImagePreview(image, policy, shareFailed, context, button),
    download: downloadFile,
  });
}
